/**
 * Business Acquisitions Page - Legacy Triangle
 */

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { 
  Briefcase,
  Search,
  FileCheck,
  Handshake,
  UserCheck,
  Target,
  Building2,
  DollarSign,
  CheckCircle,
  ArrowRight,
  Calendar,
  Shield,
  TrendingUp,
  ChevronDown,
  ChevronUp
} from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80";

const services = [
  {
    icon: Search,
    title: "Deal Sourcing",
    description: "We identify and source profitable, cash-flowing businesses that match your investment criteria.",
  },
  {
    icon: FileCheck,
    title: "Due Diligence",
    description: "Comprehensive vetting of financials, operations, and growth potential before any acquisition.",
  },
  {
    icon: Handshake,
    title: "Negotiation & Acquisition",
    description: "Expert negotiation to secure favorable terms and structure deals for maximum ROI.",
  },
  {
    icon: UserCheck,
    title: "Management Teams",
    description: "We install remote management teams so you can own without operating day-to-day.",
  },
];

const benefits = [
  {
    icon: TrendingUp,
    title: "Passive Income",
    description: "Generate consistent cash flow from profitable businesses without day-to-day involvement.",
  },
  {
    icon: Shield,
    title: "Reduced Risk",
    description: "Acquire established businesses with proven track records and existing customer bases.",
  },
  {
    icon: Building2,
    title: "Asset Diversification",
    description: "Diversify your portfolio beyond traditional investments with tangible business assets.",
  },
  {
    icon: Briefcase,
    title: "Scalable Portfolio",
    description: "Build a portfolio of multiple businesses across different industries and markets.",
  },
];

const acquisitionProcess = [
  {
    step: 1,
    title: "Initial Consultation",
    description: "We discuss your goals, budget, and preferred industries to understand your ideal acquisition.",
  },
  {
    step: 2,
    title: "Deal Sourcing",
    description: "Our team identifies potential businesses that match your criteria from our network and listings.",
  },
  {
    step: 3,
    title: "Due Diligence",
    description: "Comprehensive analysis of financials, operations, legal matters, and growth potential.",
  },
  {
    step: 4,
    title: "Negotiation",
    description: "We negotiate favorable terms, structure the deal, and handle all paperwork.",
  },
  {
    step: 5,
    title: "Closing & Transition",
    description: "Complete the acquisition and implement management team for seamless transition.",
  },
];

const faqs = [
  {
    question: "What types of businesses do you help acquire?",
    answer: "We focus on profitable, established businesses with proven cash flow. This includes service-based businesses, e-commerce companies, SaaS platforms, and traditional businesses with strong fundamentals. We avoid startups and businesses with unproven models.",
  },
  {
    question: "How long does the acquisition process take?",
    answer: "Typically, the process takes 3-6 months from initial consultation to closing. This includes deal sourcing (4-8 weeks), due diligence (4-6 weeks), negotiation (2-4 weeks), and closing (2-4 weeks). Complex deals may take longer.",
  },
  {
    question: "Do I need business experience to acquire a company?",
    answer: "While business experience is helpful, it's not required. Our management team installation service allows you to own businesses without operating them. We help you become an investor-owner, not an operator.",
  },
  {
    question: "What is the typical deal size?",
    answer: "We work with deals ranging from $500K to $5M in enterprise value. The specific amount depends on your budget, financing options, and investment goals. Larger deals are possible with the right structure.",
  },
  {
    question: "How do you find these businesses?",
    answer: "We use multiple channels including business brokers, direct outreach to business owners, our proprietary database, industry contacts, and off-market opportunities. This gives us access to deals that aren't publicly listed.",
  },
  {
    question: "What financing options are available?",
    answer: "We help structure deals using SBA loans, seller financing, private lending, and equity investors. Most deals use a combination of financing sources to minimize your out-of-pocket investment while maximizing returns.",
  },
];

export default function BusinessAcquisitions() {
  const [openFaq, setOpenFaq] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    budget: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: formData.name.split(' ')[0] || formData.name,
          lastName: formData.name.split(' ').slice(1).join(' ') || '',
          email: formData.email,
          phone: formData.phone,
          businessName: '',
          consultationType: 'Business Acquisition Consultation',
          fundingRange: formData.budget,
          yearsInBusiness: '',
          message: formData.message
        })
      });
      
      if (response.ok) {
        setSubmitSuccess(true);
        setFormData({ name: "", email: "", phone: "", budget: "", message: "" });
      }
    } catch {
      // Form submission failed silently
    }
    
    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="business-acquisitions-page">
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-center overflow-hidden pt-24">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0f2d52] via-[#0f2d52]/95 to-[#0f2d52]/80" />
        
        <div className="container relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#e8821c]/20 rounded-full px-4 py-2 mb-6">
                <Briefcase size={16} className="text-[#e8821c]" />
                <span className="text-[#e8821c] text-sm font-bold">Business Acquisitions</span>
              </div>
              
              <h1
                style={{ fontFamily: "'Montserrat', sans-serif" }}
                className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 leading-tight"
              >
                Acquire <span className="text-[#e8821c]">Profitable Businesses</span> Without The Hassle
              </h1>
              
              <p
                style={{ fontFamily: "'Inter', sans-serif" }}
                className="text-white/80 text-lg mb-6 max-w-xl"
              >
                We Find, Vet, Negotiate & Acquire Companies For You. Partner with us to handle 
                Deal Sourcing, Due Diligence, and Installing Remote Management Teams.
              </p>

              <p
                style={{ fontFamily: "'Montserrat', sans-serif" }}
                className="text-[#48d480] text-xl font-bold mb-8"
              >
                Become the Owner, Not the Operator.
              </p>

              <div className="flex flex-wrap gap-4">
                <a href="#book-call" className="inline-flex items-center gap-2 bg-[#e8821c] hover:bg-[#d67518] text-white font-bold px-6 py-3 rounded-lg transition-all">
                  Book a Consultation <ArrowRight size={18} />
                </a>
                <a href="#how-it-works" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-bold px-6 py-3 rounded-lg transition-all border border-white/20">
                  How It Works
                </a>
              </div>
            </div>

            {/* Stats Card */}
            <div className="hidden lg:block">
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20">
                <h3 className="text-white font-bold text-xl mb-6" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  Why Business Acquisitions?
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3 py-3 border-b border-white/10">
                    <CheckCircle size={20} className="text-[#e8821c] mt-0.5 flex-shrink-0" />
                    <span className="text-white/80">Skip the startup phase - acquire established cash flow</span>
                  </div>
                  <div className="flex items-start gap-3 py-3 border-b border-white/10">
                    <CheckCircle size={20} className="text-[#e8821c] mt-0.5 flex-shrink-0" />
                    <span className="text-white/80">Existing customers, systems, and revenue streams</span>
                  </div>
                  <div className="flex items-start gap-3 py-3 border-b border-white/10">
                    <CheckCircle size={20} className="text-[#e8821c] mt-0.5 flex-shrink-0" />
                    <span className="text-white/80">Leverage financing to maximize ROI</span>
                  </div>
                  <div className="flex items-start gap-3 py-3 border-b border-white/10">
                    <CheckCircle size={20} className="text-[#e8821c] mt-0.5 flex-shrink-0" />
                    <span className="text-white/80">Own multiple businesses as passive income</span>
                  </div>
                  <div className="flex items-start gap-3 py-3">
                    <CheckCircle size={20} className="text-[#e8821c] mt-0.5 flex-shrink-0" />
                    <span className="text-white/80">Build generational wealth through ownership</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-gray-50">
        <div className="container">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="h-px w-8 bg-[#e8821c]" />
              <span className="text-[#e8821c] text-xs font-bold tracking-widest uppercase">Our Services</span>
              <div className="h-px w-8 bg-[#e8821c]" />
            </div>
            <h2
              style={{ fontFamily: "'Montserrat', sans-serif" }}
              className="text-3xl md:text-4xl font-bold text-[#0f2d52] mb-4"
            >
              Full-Service <span className="text-[#e8821c]">Acquisition Support</span>
            </h2>
            <p
              style={{ fontFamily: "'Inter', sans-serif" }}
              className="text-gray-500 max-w-2xl mx-auto"
            >
              From finding the right opportunity to installing management, we handle every step of the acquisition process.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, idx) => {
              const Icon = service.icon;
              return (
                <div
                  key={service.title}
                  className="bg-white rounded-xl p-6 border border-gray-100 hover:shadow-lg hover:border-[#e8821c]/30 transition-all"
                >
                  <div className="w-14 h-14 rounded-xl bg-[#e8821c]/10 flex items-center justify-center mb-4">
                    <Icon size={28} className="text-[#e8821c]" />
                  </div>
                  <h3 className="text-[#0f2d52] font-bold text-lg mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    {service.title}
                  </h3>
                  <p className="text-gray-500 text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>
                    {service.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px w-8 bg-[#e8821c]" />
                <span className="text-[#e8821c] text-xs font-bold tracking-widest uppercase">Benefits</span>
              </div>
              <h2
                style={{ fontFamily: "'Montserrat', sans-serif" }}
                className="text-3xl md:text-4xl font-bold text-[#0f2d52] mb-6"
              >
                Why Acquire <span className="text-[#e8821c]">Existing Businesses?</span>
              </h2>
              <p
                style={{ fontFamily: "'Inter', sans-serif" }}
                className="text-gray-500 mb-8"
              >
                Acquiring an established business gives you immediate access to proven revenue, 
                existing customers, and operational systems. Skip the startup phase and start 
                generating income from day one.
              </p>

              <div className="grid sm:grid-cols-2 gap-4">
                {benefits.map((benefit, idx) => {
                  const Icon = benefit.icon;
                  return (
                    <div key={benefit.title} className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#e8821c]/10 flex items-center justify-center flex-shrink-0">
                        <Icon size={20} className="text-[#e8821c]" />
                      </div>
                      <div>
                        <h4 className="text-[#0f2d52] font-bold text-sm mb-1">{benefit.title}</h4>
                        <p className="text-gray-500 text-xs">{benefit.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-[#0f2d52] rounded-2xl p-8">
              <h3
                style={{ fontFamily: "'Montserrat', sans-serif" }}
                className="text-white font-bold text-2xl mb-6"
              >
                Investor <span className="text-[#e8821c]">Prerequisites</span>
              </h3>
              
              <div className="space-y-6">
                <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                  <div className="flex items-center gap-3 mb-2">
                    <Target size={20} className="text-[#e8821c]" />
                    <span className="text-white font-bold">Credit Score</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-white/60">Minimum Required</span>
                    <span className="text-[#e8821c] font-bold text-xl">680+</span>
                  </div>
                </div>

                <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                  <div className="flex items-center gap-3 mb-2">
                    <Building2 size={20} className="text-[#e8821c]" />
                    <span className="text-white font-bold">Total Assets</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-white/60">Minimum Required</span>
                    <span className="text-[#e8821c] font-bold text-xl">$100K+</span>
                  </div>
                </div>

                <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                  <div className="flex items-center gap-3 mb-2">
                    <DollarSign size={20} className="text-[#e8821c]" />
                    <span className="text-white font-bold">Liquidity</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-white/60">Available Capital</span>
                    <span className="text-[#e8821c] font-bold text-xl">$25-50K</span>
                  </div>
                </div>

                <div className="p-4 bg-[#e8821c]/10 rounded-lg border border-[#e8821c]/20">
                  <p className="text-white/80 text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>
                    <strong className="text-[#e8821c]">Lower credit score?</strong> We can work with investors 
                    who have lower scores if there's sufficient equity and assets. 
                    <Link to="/contact" className="text-[#e8821c] font-semibold hover:underline ml-1">
                      Contact us to discuss.
                    </Link>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 bg-[#0f2d52]">
        <div className="container">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="h-px w-8 bg-[#e8821c]" />
              <span className="text-[#e8821c] text-xs font-bold tracking-widest uppercase">The Process</span>
              <div className="h-px w-8 bg-[#e8821c]" />
            </div>
            <h2
              style={{ fontFamily: "'Montserrat', sans-serif" }}
              className="text-3xl md:text-4xl font-bold text-white mb-4"
            >
              How It <span className="text-[#e8821c]">Works</span>
            </h2>
            <p
              style={{ fontFamily: "'Inter', sans-serif" }}
              className="text-white/60 max-w-2xl mx-auto"
            >
              Our proven 5-step process takes you from initial consultation to business ownership.
            </p>
          </div>

          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-6">
            {acquisitionProcess.map((item, idx) => (
              <div
                key={item.step}
                className="relative bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10"
              >
                <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-[#e8821c] flex items-center justify-center">
                  <span className="text-white font-bold text-sm">{item.step}</span>
                </div>
                <h3
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                  className="text-white font-bold text-lg mb-2 mt-2"
                >
                  {item.title}
                </h3>
                <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-white/60 text-sm">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="container max-w-3xl">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="h-px w-8 bg-[#e8821c]" />
              <span className="text-[#e8821c] text-xs font-bold tracking-widest uppercase">FAQ</span>
              <div className="h-px w-8 bg-[#e8821c]" />
            </div>
            <h2
              style={{ fontFamily: "'Montserrat', sans-serif" }}
              className="text-3xl md:text-4xl font-bold text-[#0f2d52] mb-4"
            >
              Frequently Asked <span className="text-[#e8821c]">Questions</span>
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <div
                key={idx}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between p-5 text-left"
                >
                  <span
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                    className="font-bold text-[#0f2d52] pr-4"
                  >
                    {faq.question}
                  </span>
                  {openFaq === idx ? (
                    <ChevronUp size={20} className="text-[#e8821c] flex-shrink-0" />
                  ) : (
                    <ChevronDown size={20} className="text-gray-400 flex-shrink-0" />
                  )}
                </button>
                {openFaq === idx && (
                  <div className="px-5 pb-5">
                    <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-gray-500">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Book a Call Section */}
      <section id="book-call" className="py-20 bg-[#0f2d52]">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px w-8 bg-[#e8821c]" />
                <span className="text-[#e8821c] text-xs font-bold tracking-widest uppercase">Get Started</span>
              </div>
              <h2
                style={{ fontFamily: "'Montserrat', sans-serif" }}
                className="text-3xl md:text-4xl font-bold text-white mb-6"
              >
                Ready to <span className="text-[#e8821c]">Acquire Your First Business?</span>
              </h2>
              <p
                style={{ fontFamily: "'Inter', sans-serif" }}
                className="text-white/70 mb-8"
              >
                Book a confidential consultation with our acquisition team. We'll discuss 
                your goals, budget, and help you understand if business acquisition is 
                right for your investment strategy.
              </p>

              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#e8821c]/20 flex items-center justify-center">
                    <Calendar size={18} className="text-[#e8821c]" />
                  </div>
                  <span className="text-white">45-minute strategy session</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#e8821c]/20 flex items-center justify-center">
                    <FileCheck size={18} className="text-[#e8821c]" />
                  </div>
                  <span className="text-white">Review current acquisition opportunities</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#e8821c]/20 flex items-center justify-center">
                    <Shield size={18} className="text-[#e8821c]" />
                  </div>
                  <span className="text-white">No obligation, completely confidential</span>
                </div>
              </div>
            </div>

            <div>
              <div className="bg-white rounded-2xl p-8">
                <h3
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                  className="text-[#0f2d52] font-bold text-xl mb-6"
                >
                  Book Your Consultation
                </h3>

                {submitSuccess ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 rounded-full bg-[#e8821c]/20 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle size={32} className="text-[#e8821c]" />
                    </div>
                    <h4 className="text-[#0f2d52] font-bold text-lg mb-2">Thank You!</h4>
                    <p className="text-gray-500">
                      We've received your request. A member of our acquisitions team will 
                      contact you within 24 hours to schedule your consultation.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#e8821c]"
                        placeholder="John Smith"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#e8821c]"
                        placeholder="john@example.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                      <input
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#e8821c]"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Acquisition Budget</label>
                      <select
                        value={formData.budget}
                        onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#e8821c]"
                      >
                        <option value="">Select a range</option>
                        <option value="250000-500000">$250K - $500K</option>
                        <option value="500000-1000000">$500K - $1M</option>
                        <option value="1000000-2500000">$1M - $2.5M</option>
                        <option value="2500000-5000000">$2.5M - $5M</option>
                        <option value="5000000+">$5M+</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tell Us About Your Goals (Optional)</label>
                      <textarea
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        rows={3}
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#e8821c]"
                        placeholder="What industries interest you? What are your goals?"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full flex items-center justify-center gap-2 bg-[#e8821c] hover:bg-[#d67518] text-white font-bold py-3 px-6 rounded-lg transition-all"
                    >
                      {isSubmitting ? "Submitting..." : "Request Consultation"}
                      <ArrowRight size={18} />
                    </button>
                    <p className="text-xs text-gray-400 text-center">
                      Your information is confidential and will never be shared.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8 bg-gray-100">
        <div className="container">
          <p className="text-gray-500 text-xs text-center" style={{ fontFamily: "'Inter', sans-serif" }}>
            <strong>Disclaimer:</strong> Business acquisitions involve substantial risk. Past performance is not indicative of future results. 
            The information provided on this page is for informational purposes only and should not be considered investment advice. 
            Please consult with qualified professionals before making any investment decisions.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
